import React, { useState, useEffect, useRef } from 'react';
import { Form, ListGroup, InputGroup, Button, Spinner } from 'react-bootstrap';
import { Search, CheckCircleFill, ExclamationCircle } from 'react-bootstrap-icons';
import { useLanguage } from '../../../../../../API/Context/LanguageContext';
import { VocabSpecialtySubPicker as Vocab } from '../../../../../../ConfigFiles/multilingual/VocabSpecialtySubPicker';

// Base item shape handled by the picker widget
export interface PickerOption {
  id?: string;
  _id?: string;
  name?: string;
  specialty_name?: string;
  [key: string]: any;
}

export interface SelectedItem {
  id: string;
  name: string;
}

export interface SpecialtySubPickerProps {
  searchFunction: (query: string, signal?: AbortSignal) => Promise<PickerOption[]>;
  onSelect: (selected: SelectedItem | null, isClearAction?: boolean) => void;
  selectedValue?: string | null | undefined;
  selectedLabel?: string | null | undefined;
  placeholder?: string | undefined;  // <--- Explicitly permit undefined
  disabled?: boolean | undefined;     // <--- Explicitly permit undefined
}

const SpecialtySubPicker: React.FC<SpecialtySubPickerProps> = ({
  searchFunction,
  onSelect,
  selectedValue,
  selectedLabel,
  placeholder = "Search...",
  disabled = false,
}) => {
  const { lan } = useLanguage();
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<PickerOption[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const FIXED_HEIGHT = '38px';

  // Reset dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowResults(false);
        setQuery('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search logic with Debounce
  useEffect(() => {
    const cleanQuery = query.replace(/[0-9]/g, '');
    if (cleanQuery !== query) {
      setQuery(cleanQuery);
    }

    if (cleanQuery.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const controller = new AbortController();
    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await searchFunction(cleanQuery, controller.signal);
        setResults(data || []);
        setShowResults(true);
        setActiveIndex(0);
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error("Picker Search Error:", error);
        }
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(delayDebounceFn);
    };
  }, [query, searchFunction]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handlePick(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  const handlePick = (item: PickerOption) => {
    const resolvedId = item.id || item._id || '';
    const resolvedName = item.name || item.specialty_name || '';

    onSelect({
      id: resolvedId,
      name: resolvedName,
    });
    setQuery('');
    setShowResults(false);
  };

  // SELECTED VIEW (Locked badge state)
  if (selectedValue && selectedLabel) {
    return (
      <div
        className="p-2 border rounded bg-white d-flex align-items-center justify-content-between animate-in"
        style={{ height: FIXED_HEIGHT, borderColor: '#dee2e6' }}
      >
        <div className="d-flex align-items-center overflow-hidden">
          <CheckCircleFill className="text-success me-2 flex-shrink-0" size={14} />
          <span className="small fw-bold text-dark text-truncate">{selectedLabel}</span>
        </div>
        <Button
          variant="link"
          className="p-0 ms-2 text-decoration-none small fw-bold text-primary"
          onClick={() => onSelect(null, true)}
        >
          {Vocab.edit[lan]}
        </Button>
      </div>
    );
  }

  // SEARCH INPUT VIEW
  return (
    <div className="position-relative" ref={wrapperRef}>
      <InputGroup size="sm" style={{ height: FIXED_HEIGHT }}>
        <InputGroup.Text className="bg-white border-end-0 py-0">
          {isSearching ? (
            <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
          ) : (
            <Search size={12} className="text-muted" />
          )}
        </InputGroup.Text>
        <Form.Control
          disabled={disabled}
          placeholder={disabled ? "---" : placeholder}
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="border-start-0 ps-1 shadow-none py-0"
          style={{ fontSize: '0.85rem', height: '100%' }}
        />
      </InputGroup>

      {showResults && (
        <ListGroup className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 2000 }}>
          {results.length > 0 ? (
            results.map((item, index) => {
              const itemId = item.id || item._id || String(index);
              const itemName = item.name || item.specialty_name || '';

              return (
                <ListGroup.Item
                  key={itemId}
                  active={index === activeIndex}
                  onClick={() => handlePick(item)}
                  className="py-2 px-3 border-bottom border-light"
                  style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                >
                  <div className="fw-bold">{itemName}</div>
                </ListGroup.Item>
              );
            })
          ) : (
            <ListGroup.Item className="py-3 text-center text-muted bg-light border-0">
              <ExclamationCircle className="me-2" />
              <small>{Vocab.no_results[lan]}</small>
            </ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
};

export default SpecialtySubPicker;